import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FolderPlus, Upload, Eye, EyeOff, Trash2, Download, History, CheckCheck,
  Search, X, Move, Archive, Pencil,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import DocumentPreview, { PreviewButton } from "./DocumentPreview";
import FolderTree, { FolderBreadcrumb } from "./FolderTree";
import NewFolderModal from "./NewFolderModal";
import MassUploadModal from "./MassUploadModal";
import { fileSizeLabel, formatDateTime } from "../lib/constants";
import { sortDocuments, selectionAfterClick, SortHeader, fileExt, useElapsed } from "./DocTableUtils";

export default function AdminDocumentsTab({
  project, folders, documents, currentFolder, setCurrentFolder,
  showSuperseded, setShowSuperseded, onReload,
}) {
  const [folderModal, setFolderModal] = useState(null);
  const [renameModal, setRenameModal] = useState(null); // { id, name }
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [showMove, setShowMove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sortKey, setSortKey] = useState("title");
  const [sortDir, setSortDir] = useState("asc");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [folderDownloadId, setFolderDownloadId] = useState(null);
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const anchorRef = useRef(null);
  const folderElapsed = useElapsed(!!folderDownloadId);
  const allElapsed = useElapsed(downloading);
  const bulkElapsed = useElapsed(downloadingBulk);

  const folderById = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);

  const folderFiltered = useMemo(() => {
    if (currentFolder === "__all__") return documents;
    if (currentFolder === "__root__") return documents.filter((d) => !d.folder_id);
    return documents.filter((d) => d.folder_id === currentFolder);
  }, [documents, currentFolder]);

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q
      ? folderFiltered
      : folderFiltered.filter(
          (d) =>
            (d.title || "").toLowerCase().includes(q) ||
            (d.file_name || "").toLowerCase().includes(q) ||
            (d.drawing_number || "").toLowerCase().includes(q) ||
            (d.description || "").toLowerCase().includes(q),
        );
    return sortDocuments(list, sortKey, sortDir, {
      folder: (d) => (d.folder_id ? folderById[d.folder_id]?.name || "" : ""),
    });
  }, [folderFiltered, search, sortKey, sortDir, folderById]);

  const unreviewedCount = useMemo(
    () => documents.filter((d) => d.uploaded_by_role === "client" && !d.is_reviewed).length,
    [documents],
  );

  useEffect(() => { setSelectedIds(new Set()); setSearch(""); }, [currentFolder]);

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const selectableIds = filteredDocs.map((d) => d.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => { const n = new Set(prev); selectableIds.forEach((id) => n.delete(id)); return n; });
    } else {
      setSelectedIds((prev) => { const n = new Set(prev); selectableIds.forEach((id) => n.add(id)); return n; });
    }
  };

  const onSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const handleRowClick = (id, e) => {
    const { selected, anchor } = selectionAfterClick(selectedIds, anchorRef.current, id, e, selectableIds);
    setSelectedIds(selected);
    anchorRef.current = anchor;
  };

  const downloadSelected = async () => {
    if (selectedIds.size === 0 || busy) return;
    setBusy(true); setDownloadingBulk(true);
    try {
      const resp = await api.post(
        `/projects/${project.id}/documents/bulk-download`,
        { document_ids: Array.from(selectedIds) },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "documents.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); setDownloadingBulk(false); }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0 || busy) return;
    if (!confirm(`Permanently delete ${selectedIds.size} file(s)?`)) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/documents/bulk-delete`, { document_ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const downloadAll = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/projects/${project.id}/download-all`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${project.project_reference || "project"}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setDownloading(false); }
  };

  const moveSelected = async (targetFolderId) => {
    if (selectedIds.size === 0 || busy) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/documents/move`, {
        document_ids: Array.from(selectedIds),
        folder_id: targetFolderId || null,
      });
      setSelectedIds(new Set());
      setShowMove(false);
      onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const deleteFolder = async (fid) => {
    if (!confirm("Delete this folder? Only empty folders (no documents or sub-folders) can be deleted.")) return;
    try {
      await api.delete(`/projects/${project.id}/folders/${fid}`);
      if (currentFolder === fid) setCurrentFolder("__root__");
      onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const renameFolder = async (fid, newName) => {
    try {
      await api.patch(`/projects/${project.id}/folders/${fid}`, { name: newName });
      onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const markAllReviewed = async () => {
    try {
      await api.post(`/projects/${project.id}/documents/mark-all-reviewed`);
      onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadFolder = async (folder) => {
    if (folderDownloadId) return;
    setFolderDownloadId(folder.id);
    try {
      const resp = await api.get(
        `/projects/${project.id}/folders/${folder.id}/download`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `${folder.name || "folder"}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setFolderDownloadId(null);
    }
  };

  // Count of root-level folders (for sort_order on new root folder)
  const rootCount = folders.filter((f) => !f.parent_id).length;
  const subCount = (parentId) => folders.filter((f) => f.parent_id === parentId).length;

  const headerLabel = currentFolder === "__all__"
    ? "All documents"
    : currentFolder === "__root__"
      ? "Unfiled"
      : folderById[currentFolder]?.name || "Folder";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
      {/* Sidebar */}
      <aside>
        <div className="flex items-center justify-between mb-4">
          <div className="label">Folders</div>
          <button
            onClick={() => setFolderModal({ parentId: null, parentLabel: "" })}
            className="text-xs text-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper-hover)] flex items-center gap-1"
            data-testid="new-folder-button"
          >
            <FolderPlus size={14} /> New
          </button>
        </div>
        <FolderTree
          folders={folders}
          documents={documents}
          currentFolder={currentFolder}
          onSelect={setCurrentFolder}
          onDelete={deleteFolder}
          onRename={(folder) => setRenameModal({ id: folder.id, name: folder.name })}
          onAddSubfolder={(parentId) => setFolderModal({ parentId, parentLabel: folderById[parentId]?.name || "" })}
          onDownload={downloadFolder}
          downloadingFolderId={folderDownloadId}
          downloadElapsed={folderElapsed}
          testIdPrefix="folder"
        />

        <label className="mt-6 flex items-center gap-2 text-xs text-[color:var(--atech-text-dim)] cursor-pointer">
          <input
            type="checkbox"
            checked={showSuperseded}
            onChange={(e) => setShowSuperseded(e.target.checked)}
            className="accent-[color:var(--atech-copper)]"
            data-testid="toggle-superseded"
          />
          <History size={12} /> Show superseded
        </label>
      </aside>

      {/* Main */}
      <div>
        <FolderBreadcrumb folders={folders} currentFolder={currentFolder} onSelect={setCurrentFolder} />

        {/* Toolbar */}
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="shrink-0">
              <div className="label flex items-center gap-2">
                {headerLabel}
                {currentFolder !== "__all__" && currentFolder !== "__root__" && folderById[currentFolder] && (
                  <span className="text-[10px] text-[color:var(--atech-text-muted)] font-mono">
                    · {subCount(currentFolder)} sub-folder{subCount(currentFolder) === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="text-xs text-[color:var(--atech-text-muted)] mt-1">
                {filteredDocs.length} document{filteredDocs.length === 1 ? "" : "s"}
                {selectedIds.size > 0 && <span className="ml-2 text-[color:var(--atech-copper)]">· {selectedIds.size} selected</span>}
              </div>
            </div>
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, number…"
                className="w-full pl-7 pr-7 py-1.5 bg-transparent border border-[color:var(--atech-border)] rounded-[2px] text-xs text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)] focus:outline-none focus:border-[color:var(--atech-copper)]"
                data-testid="admin-docs-search"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-white">
                  <X size={11} />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {unreviewedCount > 0 && (
              <button
                onClick={markAllReviewed}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-[2px] border border-[color:var(--atech-copper)]/40 text-[color:var(--atech-copper)] hover:bg-[rgba(198,132,88,0.08)] transition-colors"
                data-testid="mark-all-reviewed-button"
                title={`${unreviewedCount} client upload${unreviewedCount === 1 ? "" : "s"} pending review`}
              >
                <CheckCheck size={13} /> Mark all reviewed ({unreviewedCount})
              </button>
            )}
            <button
              onClick={() => setShowMove(true)}
              disabled={busy || selectedIds.size === 0}
              className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-[2px] border transition-colors ${
                selectedIds.size > 0
                  ? "border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-white hover:border-[color:var(--atech-border-strong)]"
                  : "border-[color:var(--atech-border)]/40 text-[color:var(--atech-text-muted)] opacity-40 cursor-not-allowed"
              }`}
              data-testid="admin-move-docs-button"
              title={selectedIds.size === 0 ? "Select files to move" : `Move ${selectedIds.size} file(s)`}
            >
              <Move size={13} /> Move{selectedIds.size > 0 ? ` ${selectedIds.size}` : ""}
            </button>
            <button
              onClick={downloadSelected}
              disabled={busy || selectedIds.size === 0}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-[2px] border transition-colors ${
                selectedIds.size > 0
                  ? "border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-white hover:border-[color:var(--atech-border-strong)]"
                  : "border-[color:var(--atech-border)]/40 text-[color:var(--atech-text-muted)] opacity-40 cursor-not-allowed"
              }`}
              data-testid="bulk-download-button"
              title={selectedIds.size === 0 ? "Select files to download" : `Download ${selectedIds.size} file(s)`}
            >
              <Download size={14} /> {downloadingBulk ? `Preparing… ${bulkElapsed}s` : `Download${selectedIds.size > 0 ? ` ${selectedIds.size}` : ""}`}
            </button>
            <button
              onClick={deleteSelected}
              disabled={busy || selectedIds.size === 0}
              className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-[2px] border transition-colors ${
                selectedIds.size > 0
                  ? "border-[#F87171]/40 text-[#F87171] hover:bg-[#F87171]/10"
                  : "border-[color:var(--atech-border)]/40 text-[color:var(--atech-text-muted)] opacity-40 cursor-not-allowed"
              }`}
              data-testid="admin-bulk-delete-button"
              title={selectedIds.size === 0 ? "Select files to delete" : `Delete ${selectedIds.size} file(s)`}
            >
              <Trash2 size={13} /> Delete{selectedIds.size > 0 ? ` ${selectedIds.size}` : ""}
            </button>
            <button
              onClick={downloadAll}
              disabled={downloading}
              className="flex items-center gap-2 text-sm px-3 py-2 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-white hover:border-[color:var(--atech-border-strong)] transition-colors disabled:opacity-50"
              data-testid="admin-download-all-button"
              title="Download every file in this project as a ZIP"
            >
              <Archive size={14} /> {downloading ? `Preparing… ${allElapsed}s` : "Download all"}
            </button>
            <button onClick={() => setShowUploadModal(true)} className="btn-copper flex items-center gap-2 text-sm" data-testid="upload-document-button">
              <Upload size={14} /> Upload
            </button>
          </div>
        </div>

        <div className="card-surface overflow-hidden">
          {filteredDocs.length === 0 ? (
            <div className="p-12 text-center">
              {search ? (
                <>
                  <div className="text-sm text-[color:var(--atech-text)]">No results for &ldquo;{search}&rdquo;</div>
                  <button onClick={() => setSearch("")} className="text-xs text-[color:var(--atech-copper)] mt-2">Clear search</button>
                </>
              ) : (
                <>
                  <div className="text-sm text-[color:var(--atech-text)]">This folder is empty.</div>
                  <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 max-w-md mx-auto leading-relaxed">
                    Use the Upload button above to add drawings, reports, or correspondence.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-docs-table">
                <thead>
                  <tr className="border-b border-[color:var(--atech-border)]">
                    <th className="w-10 px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="accent-[color:var(--atech-copper)]"
                        data-testid="select-all-docs"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <SortHeader label="Name" colKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortHeader label="Type" colKey="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortHeader label="Size" colKey="file_size" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortHeader label="Date modified" colKey="created_at" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    {currentFolder === "__all__" && (
                      <SortHeader label="Folder" colKey="folder" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    )}
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((d) => (
                    <AdminDocumentRow
                      key={d.id}
                      doc={d}
                      folderName={d.folder_id ? folderById[d.folder_id]?.name : null}
                      selected={selectedIds.has(d.id)}
                      onToggle={() => toggleSelect(d.id)}
                      onRowClick={(e) => handleRowClick(d.id, e)}
                      onPreview={() => setPreviewDoc(d)}
                      onReload={onReload}
                      showFolderName={currentFolder === "__all__"}
                    />
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 text-[11px] text-[color:var(--atech-text-muted)] border-t border-[color:var(--atech-border)]/50">
                Tip: click a column heading to sort. Use Ctrl/Cmd or Shift + click to select several files, then Download.
              </div>
            </div>
          )}
        </div>
      </div>

      {renameModal && (
        <RenameFolderModal
          folder={renameModal}
          onClose={() => setRenameModal(null)}
          onSave={async (newName) => { await renameFolder(renameModal.id, newName); setRenameModal(null); }}
        />
      )}
      {folderModal && (
        <NewFolderModal
          projectId={project.id}
          parentId={folderModal.parentId}
          parentLabel={folderModal.parentLabel}
          existing={folderModal.parentId ? subCount(folderModal.parentId) : rootCount}
          onClose={() => setFolderModal(null)}
          onCreated={() => { setFolderModal(null); onReload(); }}
        />
      )}
      {showMove && (
        <MoveFolderModal
          folders={folders}
          onMove={moveSelected}
          onClose={() => setShowMove(false)}
          busy={busy}
        />
      )}
      {showUploadModal && (
        <MassUploadModal
          projectId={project.id}
          folders={folders}
          currentFolder={currentFolder === "__root__" || currentFolder === "__all__" ? null : currentFolder}
          onClose={() => setShowUploadModal(false)}
          onUploaded={() => { setShowUploadModal(false); onReload(); }}
        />
      )}
      {previewDoc && <DocumentPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />}
    </div>
  );
}

function AdminDocumentRow({ doc, folderName, selected, onToggle, onRowClick, onPreview, onReload, showFolderName }) {
  const [busy, setBusy] = useState(false);
  const needsReview = doc.uploaded_by_role === "client" && !doc.is_reviewed;
  const ext = fileExt(doc.file_name);

  const toggleVisibility = async () => {
    setBusy(true);
    try {
      await api.patch(`/documents/${doc.id}`, { visible_to_client: !doc.visible_to_client });
      onReload();
    } finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.file_name || "file"; a.click();
      URL.revokeObjectURL(url);
      if (needsReview) {
        try { await api.post(`/documents/${doc.id}/mark-reviewed`); onReload(); } catch (_) { /* non-fatal */ }
      }
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const markReviewed = async () => {
    try {
      await api.post(`/documents/${doc.id}/mark-reviewed`);
      onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async () => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${doc.id}`);
    onReload();
  };

  return (
    <tr
      className={`border-b border-white/5 cursor-default transition-colors ${selected ? "bg-[rgba(198,132,88,0.12)]" : needsReview ? "bg-[rgba(198,132,88,0.04)] hover:bg-[rgba(198,132,88,0.07)]" : "hover:bg-white/[0.02]"}`}
      data-testid={`doc-row-${doc.id}`}
      onClick={onRowClick}
      onDoubleClick={onPreview}
    >
      <td className="px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="accent-[color:var(--atech-copper)]"
          data-testid={`doc-select-${doc.id}`}
        />
      </td>
      <td className="px-4 py-3 align-middle max-w-[340px]">
        <div className="text-sm text-white truncate flex items-center gap-2 flex-wrap">
          <span className="truncate">{doc.title}</span>
          {needsReview && (
            <span
              className="text-[9px] uppercase tracking-[0.18em] text-[color:var(--atech-copper)] border border-[color:var(--atech-copper)]/50 bg-[rgba(198,132,88,0.08)] px-2 py-0.5 rounded-[2px] shrink-0"
              data-testid={`needs-review-badge-${doc.id}`}
            >
              Needs review
            </span>
          )}
          {!doc.visible_to_client && (
            <span className="text-[9px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)] border border-white/10 px-2 py-0.5 rounded-[2px] shrink-0">
              Internal
            </span>
          )}
          {!doc.is_current_version && (
            <span className="text-[9px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)] shrink-0">
              Superseded
            </span>
          )}
        </div>
        <div className="text-[11px] text-[color:var(--atech-text-muted)] font-mono truncate">{doc.file_name}</div>
      </td>
      <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{ext || "—"}</td>
      <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{fileSizeLabel(doc.file_size)}</td>
      <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{formatDateTime(doc.created_at)}</td>
      {showFolderName && (
        <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{folderName || "—"}</td>
      )}
      <td className="px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1 justify-end">
          {needsReview && (
            <button
              onClick={markReviewed}
              title="Mark as reviewed"
              className="p-2 text-[color:var(--atech-copper)] hover:text-white hover:bg-[rgba(198,132,88,0.15)] rounded-[2px]"
              data-testid={`mark-reviewed-${doc.id}`}
            >
              <CheckCheck size={14} />
            </button>
          )}
          <PreviewButton onClick={onPreview} testId={`preview-${doc.id}`} />
          <button
            disabled={busy}
            onClick={toggleVisibility}
            title={doc.visible_to_client ? "Hide from client" : "Show to client"}
            className="p-2 text-[color:var(--atech-text-dim)] hover:text-white hover:bg-white/5 rounded-[2px]"
            data-testid={`toggle-visibility-${doc.id}`}
          >
            {doc.visible_to_client ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          {doc.storage_path && (
            <button onClick={download} title="Download" className="p-2 text-[color:var(--atech-text-dim)] hover:text-white hover:bg-white/5 rounded-[2px]" data-testid={`download-${doc.id}`}>
              <Download size={14} />
            </button>
          )}
          <button onClick={remove} title="Delete" className="p-2 text-[color:var(--atech-text-dim)] hover:text-[#F87171] hover:bg-white/5 rounded-[2px]" data-testid={`delete-${doc.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}


function RenameFolderModal({ folder, onClose, onSave }) {
  const [name, setName] = useState(folder.name);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try { await onSave(trimmed); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="rename-folder-modal">
      <div className="card-surface w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="label">Rename folder</div>
        <form onSubmit={submit} className="space-y-4">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]"
            data-testid="rename-folder-input"
          />
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button type="submit" disabled={busy || !name.trim()} className="btn-copper text-sm" data-testid="rename-folder-save">
              {busy ? "Saving…" : "Rename"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MoveFolderModal({ folders, onMove, onClose, busy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="move-folder-modal">
      <div className="card-surface w-full max-w-sm p-6 space-y-4">
        <div className="label">Move selected files to…</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          <button
            onClick={() => onMove(null)}
            disabled={busy}
            className="w-full text-left px-3 py-2 text-sm rounded-[2px] hover:bg-white/5 text-[color:var(--atech-text-dim)]"
            data-testid="move-to-root"
          >
            / Root (unfiled)
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              onClick={() => onMove(f.id)}
              disabled={busy}
              className="w-full text-left px-3 py-2 text-sm rounded-[2px] hover:bg-white/5 text-[color:var(--atech-text)]"
              style={{ paddingLeft: `${(f.depth || 0) * 16 + 12}px` }}
              data-testid={`move-to-folder-${f.id}`}
            >
              {f.name}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
        </div>
      </div>
    </div>
  );
}
